import { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { Upload, Trash2, Check, Loader2, ImageIcon, GripVertical, Factory, Warehouse } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: "production", label: "Výrobné priestory", icon: Factory },
  { key: "warehouse", label: "Skladovacie priestory", icon: Warehouse },
];

const shrink = (file, max = 2200) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const s = Math.min(1, max / Math.max(img.width, img.height));
    if (s === 1 && file.size < 2.5e6) { URL.revokeObjectURL(url); return resolve(file); }
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => { URL.revokeObjectURL(url); resolve(b ? new File([b], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }) : file); }, "image/jpeg", 0.88);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const PhotoCard = ({ p, i, sections, onCaption, onDelete, onSection, onDragEnd }) => {
  const controls = useDragControls();
  const [caption, setCaption] = useState(p.caption);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setCaption(p.caption), [p.caption]);
  const dirty = caption !== p.caption;

  const save = async () => { setSaving(true); await onCaption(p.id, caption); setSaving(false); };

  return (
    <Reorder.Item value={p} dragListener={false} dragControls={controls} onDragEnd={onDragEnd}
      data-testid={`admin-photo-${i}`} data-photo-id={p.id}
      whileDrag={{ scale: 1.01, boxShadow: "0 18px 40px rgba(0,0,0,0.5)", borderColor: "#FF5500" }}
      className="border border-steelborder bg-steelcard flex gap-3 p-4 select-none">
      <button onPointerDown={(e) => controls.start(e)} data-testid={`admin-photo-drag-${i}`} title="Ťahaním zmeňte poradie"
        aria-label="Presunúť"
        className="shrink-0 w-8 flex items-center justify-center text-slate-600 hover:text-safety cursor-grab active:cursor-grabbing touch-none transition-colors">
        <GripVertical className="w-5 h-5" />
      </button>
      <img src={`${API}${p.thumb_url}`} alt="" draggable={false} className="hidden sm:block w-44 h-32 object-cover border border-steelborder shrink-0" />
      <div className="flex-1 flex flex-col gap-3 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[10px] tracking-[0.25em] text-safety">{String(i + 1).padStart(2, "0")}</span>
          <div className="flex items-center gap-1">
            {SECTIONS.map((s) => {
              const active = sections[s.key]?.id === p.id;
              return (
                <button key={s.key} onClick={() => onSection(s.key, active ? null : p.id)} data-testid={`admin-photo-section-${s.key}-${i}`}
                  data-active={active} title={active ? `Použité v kapitole ${s.label} – kliknutím zrušíte` : `Použiť v kapitole ${s.label}`}
                  className={`flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 border transition-colors ${active ? "border-blueprint text-blueprint bg-blueprint/10" : "border-steelborder text-slate-500 hover:text-slate-200 hover:border-slate-400"}`}>
                  <s.icon className="w-3.5 h-3.5" /> <span className="hidden md:inline">{s.label}</span>
                </button>
              );
            })}
            {confirming ? (
              <span className="flex items-center gap-1 ml-2" data-testid={`admin-photo-delete-confirm-${i}`}>
                <button onClick={() => onDelete(p.id)} data-testid={`admin-photo-delete-yes-${i}`} className="font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 bg-red-600 text-white hover:bg-red-500 transition-colors">Vymazať</button>
                <button onClick={() => setConfirming(false)} data-testid={`admin-photo-delete-no-${i}`} className="font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-steelborder text-slate-400 hover:text-slate-100 transition-colors">Zrušiť</button>
              </span>
            ) : (
              <button onClick={() => setConfirming(true)} data-testid={`admin-photo-delete-${i}`} title="Vymazať fotku"
                className="p-1.5 ml-2 border border-steelborder text-slate-500 hover:text-red-400 hover:border-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={300} placeholder="Popis fotky (voliteľný)"
            onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); }}
            data-testid={`admin-photo-caption-${i}`}
            className="flex-1 min-w-0 bg-gunmetal border border-steelborder px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-safety transition-colors" />
          <button onClick={save} disabled={!dirty || saving} data-testid={`admin-photo-save-${i}`}
            className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase px-3 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500 hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-emerald-400 transition-colors">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Uložiť
          </button>
        </div>
        <p className="font-mono text-[10px] text-slate-600 truncate">{p.width}×{p.height} px</p>
      </div>
    </Reorder.Item>
  );
};

export const GalleryAdmin = ({ authed, setError }) => {
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const inputRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const savedOrder = useRef("");

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetch(`${API}/api/gallery`).then((r) => r.json());
      setItems(d.items);
      setSections(d.sections || {});
      savedOrder.current = d.items.map((p) => p.id).join(",");
    } catch { setError("Galériu sa nepodarilo načítať."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async () => {
    if (!queue.length) return;
    setUploading(true);
    try {
      for (let i = 0; i < queue.length; i++) {
        setProgress(`${i + 1} / ${queue.length}`);
        const f = await shrink(queue[i]);
        const fd = new FormData();
        fd.append("file", f);
        fd.append("caption", queue.length === 1 ? caption : caption ? `${caption} (${i + 1})` : "");
        const added = await authed("/gallery", { method: "POST", body: fd, headers: {} }, true);
        setItems((prev) => [...prev, added]);
      }
      setQueue([]); setCaption("");
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) { setError(err.message); }
    finally { setUploading(false); setProgress(""); }
  };

  const saveCaption = async (id, text) => {
    try {
      await authed(`/gallery/${id}`, { method: "PATCH", body: JSON.stringify({ caption: text }) });
      setItems((prev) => prev.map((p) => (p.id === id ? { ...p, caption: text.trim() } : p)));
    } catch (err) { setError(err.message); }
  };

  const persistOrder = async () => {
    const ids = itemsRef.current.map((p) => p.id);
    const sig = ids.join(",");
    if (sig === savedOrder.current) return;
    try {
      await authed("/gallery/reorder", { method: "PUT", body: JSON.stringify({ ids }) });
      savedOrder.current = sig;
    } catch (err) { setError(err.message); load(); }
  };

  const setSection = async (section, photoId) => {
    try {
      const d = await authed("/gallery/sections", { method: "PUT", body: JSON.stringify({ section, photo_id: photoId }) });
      setSections(d.sections);
    } catch (err) { setError(err.message); }
  };

  const remove = async (id) => {
    try {
      await authed(`/gallery/${id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((p) => p.id !== id));
      setSections((prev) => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, v?.id === id ? null : v])));
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="space-y-8">
      <div data-testid="gallery-upload-panel" className="border border-dashed border-steelborder bg-steelcard/60 p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Upload className="w-5 h-5 text-safety" />
          <h2 className="font-heading font-bold text-xl uppercase text-slate-100">Pridať fotografie</h2>
        </div>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
          <label className="flex items-center gap-3 border border-steelborder bg-gunmetal px-3 py-2.5 cursor-pointer hover:border-safety transition-colors">
            <ImageIcon className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="text-sm text-slate-400 truncate">{queue.length ? `${queue.length} ${queue.length === 1 ? "súbor" : queue.length < 5 ? "súbory" : "súborov"} vybraných` : "Vybrať obrázky (JPG, PNG, WEBP)"}</span>
            <input ref={inputRef} type="file" accept="image/*" multiple className="sr-only" data-testid="gallery-file-input"
              onChange={(e) => setQueue(Array.from(e.target.files || []))} />
          </label>
          <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={300} placeholder="Popis (voliteľný)"
            data-testid="gallery-upload-caption"
            className="bg-gunmetal border border-steelborder px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-safety transition-colors" />
          <button onClick={upload} disabled={!queue.length || uploading} data-testid="gallery-upload-button"
            className="flex items-center justify-center gap-2 bg-safety hover:bg-safety-hover disabled:opacity-40 text-ink font-heading font-bold uppercase tracking-wider px-6 py-2.5 transition-colors">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> {progress}</> : "Nahrať"}
          </button>
        </div>
        <p className="font-mono text-[10px] text-slate-600">Veľké fotky sa pred nahraním automaticky zmenšia. Popis môžete upraviť aj neskôr pri každej fotke.</p>
      </div>

      <div data-testid="section-images-panel" className="grid sm:grid-cols-2 gap-3">
        {SECTIONS.map((s) => (
          <div key={s.key} data-testid={`section-image-${s.key}`} className="border border-steelborder bg-steelcard p-4 flex items-center gap-4">
            {sections[s.key] ? (
              <img src={`${API}${sections[s.key].thumb_url}`} alt="" className="w-24 h-16 object-cover border border-steelborder shrink-0" />
            ) : (
              <div className="w-24 h-16 border border-dashed border-steelborder grid place-items-center text-slate-600 shrink-0"><s.icon className="w-5 h-5" /></div>
            )}
            <div className="min-w-0">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Fotka v kapitole</p>
              <p className="font-heading font-bold uppercase text-slate-100">{s.label}</p>
              <p className="text-xs text-slate-500 truncate">{sections[s.key] ? (sections[s.key].caption || "bez popisu") : "Používa sa ukážková fotka — vyberte vlastnú tlačidlom pri fotke nižšie"}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3" data-testid="admin-gallery-list">
        <div className="flex items-center justify-between">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-slate-500">Fotografie <span className="text-slate-300" data-testid="admin-gallery-count">{items.length}</span></p>
          {items.length > 1 && <p className="font-mono text-[10px] text-slate-600 flex items-center gap-1"><GripVertical className="w-3.5 h-3.5" /> ťahaním za úchyt zmeníte poradie</p>}
        </div>
        <Reorder.Group axis="y" values={items} onReorder={setItems} className="space-y-3">
          {items.map((p, i) => (
            <PhotoCard key={p.id} p={p} i={i} sections={sections} onCaption={saveCaption} onDelete={remove} onSection={setSection} onDragEnd={persistOrder} />
          ))}
        </Reorder.Group>
        {!loading && items.length === 0 && (
          <div data-testid="admin-gallery-empty" className="border border-steelborder bg-steelcard py-14 text-center font-mono text-xs tracking-[0.2em] uppercase text-slate-500">Zatiaľ žiadne fotografie</div>
        )}
      </div>
    </div>
  );
};
